/**
 * SMS consent copy for the surfaces that render it. The `version` string travels
 * with every preference write, so a wording change is a new entry with a new
 * version rather than an edit to an existing one — the recorded version has to
 * keep meaning what the user actually agreed to.
 */
import React from 'react';

import { Link } from '@opendoor/atoms/link';

import { ACCOUNT_SMS_CONSENT_DESCRIPTION } from '../AccountForm/AccountFormV2/marketingConsent';
import { OFFER_SMS_CONSENT_DESCRIPTION } from '../consent-copy';

interface CtaAgreementLinkedProps {
  ctaText: string;
  tosAnalyticsName: string;
  privacyAnalyticsName: string;
  tosUrl?: string;
  privacyUrl?: string;
}

/** Checkbox rendered on registration; only drivesPreference entries update marketing opt-in. */
export type SmsConsentCheckboxSpec = {
  label: string;
  name: string;
  analyticsSuffix: string;
  drivesPreference?: boolean;
};

export const CtaAgreementLinked: React.FC<CtaAgreementLinkedProps> = ({
  ctaText,
  tosAnalyticsName,
  privacyAnalyticsName,
  tosUrl = 'https://www.opendoor.com/w/terms',
  privacyUrl = 'https://www.opendoor.com/w/privacy',
}) => (
  <>
    By pressing &ldquo;{ctaText},&rdquo; I agree to Opendoor&rsquo;s{' '}
    <Link
      analyticsName={tosAnalyticsName}
      href={tosUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Opens a new page with Opendoor’s terms of service"
    >
      Terms of Service
    </Link>{' '}
    and{' '}
    <Link
      analyticsName={privacyAnalyticsName}
      href={privacyUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Opens a new page with Opendoor’s privacy policy"
    >
      Privacy Policy
    </Link>
    .
  </>
);

/**
 * Dual account vs offer SMS consent for cosmos /login (Twilio RCS).
 * Offer drives marketingSmsOptOut today; account is display-only until wired.
 *
 * The two strings live in the V2 registry (`AccountFormV2/marketingConsent.ts`),
 * which both arms of the `auth-fe-account-form` flag read, so one
 * `marketing_sms_v3` record can never mean two different sentences.
 */
export const MARKETING_SMS_CONSENT_V3 = {
  version: 'marketing_sms_v3',
  checkboxes: [
    {
      label: ACCOUNT_SMS_CONSENT_DESCRIPTION,
      name: 'account-sms-opt-in',
      analyticsSuffix: 'account-sms-checkbox',
    },
    {
      label: OFFER_SMS_CONSENT_DESCRIPTION,
      name: 'offer-sms-opt-in',
      analyticsSuffix: 'offer-sms-checkbox',
      drivesPreference: true,
    },
  ] satisfies ReadonlyArray<SmsConsentCheckboxSpec>,
  ctaAgreement: (ctaText: string) =>
    `By pressing “${ctaText},” I agree to Opendoor’s Terms of Service and Privacy Policy.`,
  full: (ctaText: string) =>
    `${ACCOUNT_SMS_CONSENT_DESCRIPTION} ${OFFER_SMS_CONSENT_DESCRIPTION} By pressing “${ctaText},” I agree to Opendoor’s Terms of Service and Privacy Policy.`,
  CtaAgreementLinked,
};
